// ============================================================================
// BUSINESS HOURS — timezone-aware elapsed-time math for SLA breach detection
//
// Single source of truth for "how long has this referral been stuck during
// working hours?" Used by every operational SLA card (Ops Command Center,
// Triage Queue Health, Ops Home).
//
// Zero-PHI: operates only on timestamps + tenant business-hours config.
// ============================================================================

/**
 * Wall-clock components of an instant in a target timezone.
 * weekday: 0=Sun..6=Sat — matches the workingDays convention.
 *
 * @param {number} ms - epoch milliseconds
 * @param {string} tz - IANA timezone, e.g. 'Europe/Ljubljana'
 * @returns {{ y:number, mo:number, d:number, h:number, min:number, weekday:number }}
 */
export function tzWallClock(ms, tz) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', weekday: 'short',
  });
  const p = {};
  for (const part of dtf.formatToParts(new Date(ms))) p[part.type] = part.value;
  const wd = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    y: +p.year, mo: +p.month, d: +p.day,
    h: +p.hour, min: +p.minute, weekday: wd[p.weekday],
  };
}

/**
 * Business-hours elapsed between two instants, in the tenant's timezone.
 * Sums only working-day, working-hour overlap. DST-safe: civil dates are
 * differenced as UTC midnights and the weekday is read from the civil date.
 *
 * @param {number} startMs
 * @param {number} endMs
 * @param {{ timezone?:string, workingDays?:number[], startHour?:number, endHour?:number }} bh
 * @returns {number} elapsed business hours (fractional)
 */
export function businessHoursElapsed(startMs, endMs, bh) {
  if (!startMs || !endMs || endMs <= startMs) return 0;

  const tz = bh?.timezone || 'America/New_York';
  const workDays = new Set(bh?.workingDays ?? [1, 2, 3, 4, 5]);
  const startHour = bh?.startHour ?? 8;
  const endHour = bh?.endHour ?? 17;
  if (endHour - startHour <= 0) return (endMs - startMs) / (60 * 60 * 1000);

  const s = tzWallClock(startMs, tz);
  const e = tzWallClock(endMs, tz);

  const sUTC = Date.UTC(s.y, s.mo - 1, s.d);
  const eUTC = Date.UTC(e.y, e.mo - 1, e.d);
  const dayCount = Math.round((eUTC - sUTC) / (24 * 60 * 60 * 1000));

  let totalHours = 0;
  for (let i = 0; i <= dayCount; i++) {
    const civil = new Date(sUTC + i * 24 * 60 * 60 * 1000);
    if (!workDays.has(civil.getUTCDay())) continue;

    let winStart = startHour;
    let winEnd = endHour;
    if (i === 0) winStart = Math.max(startHour, s.h + s.min / 60);
    if (i === dayCount) winEnd = Math.min(endHour, e.h + e.min / 60);

    if (winEnd > winStart) totalHours += winEnd - winStart;
  }

  return totalHours;
}
